import React, { useState } from 'react';
import { Save, X, FileText, MessageSquare, Megaphone, StickyNote, Type } from 'lucide-react';
import toast from 'react-hot-toast';

/** Contenuto appena creato, come lo restituisce il backend. */
export interface ContenutoCreato {
  id: string;
  title: string;
  hook?: string;
  body?: string;
  cta?: string;
  notes?: string;
  status: string;
  created_at?: string;
  updated_at?: string;
}

interface Props {
  clientId: string;
  onCreated: (contenuto: ContenutoCreato) => void;
  onClose: () => void;
}

/**
 * Modulo con cui il cliente scrive un contenuto nuovo dalla sua pagina.
 *
 * Come la scheda di modifica vive dentro la lista "Gestione script", non in un
 * popup. Il contenuto nasce come testo confermato: conta nei testi del mese e
 * finisce subito tra quelli da registrare.
 */
const ClientNewContentForm: React.FC<Props> = ({ clientId, onCreated, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: '', hook: '', body: '', cta: '', notes: '' });

  const set = (campo: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setFormData((prev) => ({ ...prev, [campo]: e.target.value }));

  const pronto = formData.title.trim().length > 0 && formData.body.trim().length > 0;

  const handleSave = async () => {
    if (!pronto) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/contents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Contenuto aggiunto!');
        onCreated(data.data);
      } else {
        toast.error(data.message || 'Errore nel salvataggio');
      }
    } catch {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const campoClasse =
    'w-full text-sm text-slate-700 dark:text-slate-200 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-300 dark:border-yellow-700 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-yellow-400 leading-relaxed';

  const etichetta = (Icona: typeof FileText, colore: string, testo: string, obbligatorio = false) => (
    <div className="flex items-center gap-2 mb-1.5">
      <Icona className={`w-3.5 h-3.5 ${colore}`} />
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        {testo}
      </span>
      {obbligatorio && <span className="text-xs text-slate-400 dark:text-slate-500">(obbligatorio)</span>}
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Scrivi il testo del reel: lo trovi poi in «Da Registrare» e conta tra i contenuti del mese.
      </p>

      <div className="rounded-xl border border-slate-200 dark:border-gray-700 overflow-hidden divide-y divide-slate-200 dark:divide-gray-700">
        <div className="p-3 sm:p-4">
          {etichetta(Type, 'text-slate-500', 'Titolo', true)}
          <input
            type="text"
            value={formData.title}
            onChange={set('title')}
            maxLength={500}
            className={campoClasse}
            placeholder="Di cosa parla il contenuto?"
            autoFocus
          />
        </div>
        <div className="p-3 sm:p-4">
          {etichetta(MessageSquare, 'text-amber-500', 'Hook')}
          <textarea value={formData.hook} onChange={set('hook')} rows={2} className={`${campoClasse} resize-y`} placeholder="La frase d'apertura che cattura l'attenzione..." />
        </div>
        <div className="p-3 sm:p-4">
          {etichetta(FileText, 'text-blue-500', 'Corpo', true)}
          <textarea value={formData.body} onChange={set('body')} rows={8} className={`${campoClasse} resize-y`} placeholder="Scrivi il testo del contenuto..." />
        </div>
        <div className="p-3 sm:p-4">
          {etichetta(Megaphone, 'text-green-500', 'Call to Action')}
          <input type="text" value={formData.cta} onChange={set('cta')} className={campoClasse} placeholder="Cosa deve fare chi guarda?" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-gray-700 p-3 sm:p-4">
        {etichetta(StickyNote, 'text-slate-400', 'Note')}
        <textarea value={formData.notes} onChange={set('notes')} rows={3} className={`${campoClasse} resize-y`} placeholder="Indicazioni per le riprese, riferimenti..." />
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
        <button
          onClick={onClose}
          disabled={saving}
          className="px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 dark:hover:bg-gray-600 rounded-xl transition-colors flex items-center justify-center gap-2 min-h-[44px]"
        >
          <X className="w-4 h-4" />
          Annulla
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !pronto}
          className="px-5 py-2.5 text-sm font-medium text-slate-800 bg-gradient-to-b from-[#fff7a8] to-[#fdf751] hover:from-[#fdf751] hover:to-[#f5ef00] rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 min-h-[44px]"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Salvataggio...' : 'Aggiungi contenuto'}
        </button>
      </div>
    </div>
  );
};

export default ClientNewContentForm;
